import React, { useState } from "react";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight, ChevronDown, GraduationCap, Sparkles } from "lucide-react";

const RES_ARC = [
  ["WKS 1–2", "Foundations", "Online · creative-business fundamentals"],
  ["WKS 3–6", "Domain deep-dive", "Hybrid · 1-on-1 mentor + sector masterclasses"],
  ["WKS 7–8", "Cross-pollination", "In-person · collaborative work + peer briefs"],
  ["WKS 9–10", "Capstone", "Real-world brand project, exhibition or launch"],
];

const RES_FORMAT = [
  ["DURATION", "10 wks · hybrid"],
  ["COHORT", "5–8 fellows · 2/yr"],
  ["SECTORS", "Music · Film · Theatre · Art · Dance · Writing · Design"],
  ["CITY NETWORK", "6 cities · studio + gear access"],
  ["TUITION", "₹1.5L · ₹85K for Pass members"],
  ["SCHOLARSHIPS", "2 fully-funded / cohort"],
];

const ACAD_COURSES = [
  ["8 WKS", "Music Business 101", "Deals · rights · 5 income streams · contracts"],
  ["10 WKS", "Rights, Royalties & Publishing", "Master vs publishing · IPRS/PPL · sync"],
  ["8 WKS", "Touring & Live Operations", "Booking · riders · settlements · India + intl"],
  ["8 WKS", "Marketing, Brand & Audience", "Algorithm · indie PR · D2C · partnerships"],
  ["10 WKS", "Production & Studio Business", "Producer deals · splits · clearance · P&L"],
  ["12 WKS", "Music Career as a Business · Capstone", "Capstone · team · tax · catalogue · exit"],
];

const PARTNERS = ["Bluprint","BTOS","OML","Startist","Molfa","Big Bad Wolf","All Things Loud"];

const TABS = [
  { k: "residency", label: "The Residency", caption: "10-week cross-creative cohort" },
  { k: "academy", label: "The Academy", caption: "6 courses · Indian music business" },
];

export default function Education() {
  const [tab, setTab] = useState("residency");
  const [openCourse, setOpenCourse] = useState(0);

  // Application form state — shared shape; `program` & `course` set per submission
  const [form, setForm] = useState({
    name: "", email: "", discipline: "music", city: "Mumbai",
    statement: "", portfolio: "", course: ACAD_COURSES[0][1],
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (program) => {
    if (!form.name || !form.email || !form.statement) {
      toast.error("Name, email and statement are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, program, course: program === "academy" ? form.course : "" };
      await api.post("/applications", payload);
      toast.success("Application received. We read every word.");
      setForm({ ...form, name: "", email: "", statement: "" });
    } catch { toast.error("Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="THE RESIDENCY" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <SectionLabel><GraduationCap size={11} /> Learn · The Residency at anArtizen</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            Level up <br /> on the <span className="hl-coral">work.</span>
          </h1>
          <p className="mt-10 max-w-3xl text-white/80 text-base md:text-lg leading-relaxed">
            <strong>The Residency</strong> — a 10-week, cross-disciplinary cohort that turns artists into operators. <strong>The Academy</strong> — six courses built with India's music industry to produce graduates who get hired, not who apply. Pick the door you need.
          </p>
        </div>
      </section>

      {/* TABS */}
      <section className="border-b border-border sticky top-14 z-30 bg-background/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-4 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`education-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`btn-pill ${tab === t.k ? "btn-coral" : ""}`}
            >
              {t.label}
            </button>
          ))}
          <span className="meta-mono ml-2 self-center !text-white/50">{TABS.find((t) => t.k === tab).caption}</span>
        </div>
      </section>

      {/* RESIDENCY */}
      {tab === "residency" && (
        <>
          <section className="border-b border-border">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
              <SectionLabel>Programme Arc</SectionLabel>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-10">
                10 weeks. 5–8 artists. <span className="hl-cyan">Seven sectors.</span>
              </h2>
              <div className="grid md:grid-cols-4 gap-px bg-border">
                {RES_ARC.map(([w, t, d]) => (
                  <div key={w} className="bg-background p-6 md:p-8 min-h-[220px]">
                    <p className="meta-mono !text-primary mb-4">{w}</p>
                    <p className="font-display text-2xl md:text-3xl font-bold leading-tight">{t}</p>
                    <p className="text-white/65 text-sm mt-4">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="border-b border-border bg-[#0c0c0e]">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
              {RES_FORMAT.map(([k, v]) => (
                <div key={k} className="bg-[#0c0c0e] p-6">
                  <p className="meta-mono mb-2">— {k}</p>
                  <p className="font-display text-xl md:text-2xl font-bold leading-tight">{v}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16">
              <SectionLabel>Apply · The Residency</SectionLabel>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-8">
                Tell us what you'd <span className="hl-coral">build</span> in 10 weeks.
              </h2>
              <ApplicationForm form={form} setForm={setForm} submitting={submitting} onSubmit={() => submit("residency")} program="residency" testidPrefix="res" />
            </div>
          </section>
        </>
      )}

      {/* ACADEMY */}
      {tab === "academy" && (
        <>
          <section className="border-b border-border">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
              <SectionLabel>Six courses · Along the music-business value chain</SectionLabel>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-10">
                Learn the business. <span className="hl-cyan">Get hired.</span>
              </h2>
              <div className="border border-border divide-y divide-border">
                {ACAD_COURSES.map(([w, t, d], i) => (
                  <div key={t}>
                    <button
                      data-testid={`course-${i}`}
                      onClick={() => setOpenCourse(openCourse === i ? -1 : i)}
                      className="w-full p-6 md:p-8 flex items-center justify-between gap-6 hover:bg-white/[0.02] text-left"
                    >
                      <div className="flex items-center gap-6 md:gap-10">
                        <span className="meta-mono !text-primary">{w}</span>
                        <span className="font-display text-2xl md:text-3xl font-bold">{t}</span>
                      </div>
                      <ChevronDown size={18} className={`transition ${openCourse === i ? "rotate-180" : ""}`} />
                    </button>
                    {openCourse === i && (
                      <div className="px-6 md:px-8 pb-8 grid md:grid-cols-2 gap-8">
                        <p className="text-white/80 leading-relaxed">{d}</p>
                        <div className="border-l border-border pl-6 space-y-2 text-sm">
                          <Row k="Tuition" v="₹40–80K" />
                          <Row k="Format" v="Hybrid · live" />
                          <Row k="Placements" v="Partners hire top graduates first" />
                          <Row k="Scholarships" v="Co-sponsored seats available" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="border-b border-border bg-[#0c0c0e]">
            <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12">
              <SectionLabel>7 Industry Partners</SectionLabel>
              <div className="flex flex-wrap gap-3">
                {PARTNERS.map((p) => (
                  <span key={p} className="px-5 py-3 border border-border font-mono text-xs uppercase tracking-[0.15em]">{p}</span>
                ))}
              </div>
              <p className="meta-mono mt-6">— Partners teach modules and interview top graduates first.</p>
            </div>
          </section>

          <section>
            <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16">
              <SectionLabel>Apply · The Academy</SectionLabel>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-8">
                Pick a course. <span className="hl-coral">Get to work.</span>
              </h2>
              <div className="bg-background border border-border p-6 mb-px">
                <p className="meta-mono mb-3">— COURSE</p>
                <select
                  data-testid="ac-course"
                  value={form.course}
                  onChange={(e) => setForm({ ...form, course: e.target.value })}
                  className="w-full bg-transparent outline-none text-lg font-light border-b border-border focus:border-primary py-2"
                >
                  {ACAD_COURSES.map(([, t]) => <option key={t} value={t} className="bg-background">{t}</option>)}
                </select>
              </div>
              <ApplicationForm form={form} setForm={setForm} submitting={submitting} onSubmit={() => submit("academy")} program="academy" testidPrefix="ac" />
            </div>
          </section>
        </>
      )}

      <section className="border-t border-border">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16 text-center">
          <p className="font-serif-italic text-xl md:text-2xl text-white/85">
            Pass members get tuition discounts on both programmes. <a href="/artizen-pass" className="hl-coral not-italic font-display font-bold">Get the Pass</a> first if you can.
          </p>
        </div>
      </section>
    </PageShell>
  );
}

function ApplicationForm({ form, setForm, submitting, onSubmit, program, testidPrefix }) {
  return (
    <div className="grid md:grid-cols-2 gap-px bg-border">
      <F label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid={`${testidPrefix}-name`} />
      <F label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid={`${testidPrefix}-email`} />
      <F label="Discipline" v={form.discipline} on={(v) => setForm({ ...form, discipline: v })} testid={`${testidPrefix}-discipline`} />
      <F label="City" v={form.city} on={(v) => setForm({ ...form, city: v })} testid={`${testidPrefix}-city`} />
      {program === "residency" && (
        <>
          <F label="Portfolio / link" v={form.portfolio} on={(v) => setForm({ ...form, portfolio: v })} testid={`${testidPrefix}-portfolio`} />
          <div className="bg-background p-6">
            <p className="meta-mono mb-3">— TUITION</p>
            <p className="font-display text-3xl font-bold">₹1.5L</p>
            <p className="meta-mono mt-2 !text-primary">₹85K with Artizen Pass</p>
          </div>
        </>
      )}
      <div className="md:col-span-2 bg-background p-6">
        <p className="meta-mono mb-3">— STATEMENT (300 words max)</p>
        <textarea
          data-testid={`${testidPrefix}-statement`}
          rows={6}
          value={form.statement}
          onChange={(e) => setForm({ ...form, statement: e.target.value })}
          placeholder="What do you make? What would you build here?"
          className="w-full bg-transparent outline-none text-base md:text-lg font-light leading-relaxed border-b border-border focus:border-primary py-3"
        />
      </div>
      <div className="md:col-span-2 bg-background p-6 flex justify-end">
        <button data-testid={`${testidPrefix}-submit`} disabled={submitting} onClick={onSubmit} className="btn-pill btn-coral">
          {submitting ? "Sending…" : <>Apply <ArrowRight size={14} /></>}
        </button>
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", testid }) {
  return (
    <div className="bg-background p-6">
      <p className="meta-mono mb-3">— {label.toUpperCase()}</p>
      <input data-testid={testid} type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-transparent outline-none text-lg font-light border-b border-border focus:border-primary py-2" />
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex items-center justify-between text-sm"><span className="text-white/70">{k}</span><span className="font-mono text-xs text-primary tracking-wider">{v}</span></div>;
}
